import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue';
import axios, { type AxiosResponse } from 'axios';
import config from '../config/ConfigProvider';
import type { GetTasksResponse, Task } from '@ilteducation/the-publisher-types';
import IdIcon from '@/components/IdIcon.vue';
import CreateNewPublisherTaskForm from '@/components/the-publisher/CreateNewPublisherTaskForm.vue';
import type { Job } from '@ilteducation/studio-jobs-types';
import { GetExecutionInfoResponse } from '@ilteducation/epub-guide-generator-types';
import { BookV1 } from '@ilteducation/content-registry-types';
import { useRoute } from 'vue-router/composables';


export default /*#__PURE__*/_defineComponent({
  __name: 'ThePublisher',
  setup(__props) {

const isLoadingTasks = ref(false);
const tasks = ref<Task[]>();
const studioJobs = ref<Record<string, Job>>({});
const epubExecutions = ref<Record<string, GetExecutionInfoResponse>>({});
const books = ref<Record<string, BookV1>>({});
const route = useRoute();

const bookIdParameter = computed(() => route.query.bookId as string | undefined);
const langParameter = computed(() => route.query.lang as string | undefined);
const pagesParameter = computed(() => parseInt((route.query.pages as string | undefined) || '0'));
const titleParameter = computed(() => route.query.title as string | undefined);
const tabParameter = computed(() => route.query.tab as string | undefined);

// AWS Step Functions retains execution history for ~90 days, after which getExecutionInfo only
// returns ExecutionDoesNotExist. We hide tasks past this window so we never fire a lookup that
// can only fail. The cutoff is set to 85 (not 90) on purpose: task.createdAt is just a proxy for
// the execution's retention clock (AWS measures from execution close, and ~90 days is approximate,
// not an exact boundary), so a 5-day buffer keeps tasks sitting right at the edge from slipping
// through and failing.
const TASK_RETENTION_DAYS = 85;
const isWithinRetentionWindow = (task: Task): boolean => {
  const createdAtMs = new Date(task.createdAt).getTime();
  if (Number.isNaN(createdAtMs)) return false;
  const ageMs = Date.now() - createdAtMs;
  return ageMs <= TASK_RETENTION_DAYS * 24 * 60 * 60 * 1000;
};

const refresh = async () => {
  try {
    isLoadingTasks.value = true;
    const allTasks: Task[] = [];
    let cursor: string | undefined = undefined;
    do {
      const response: AxiosResponse<GetTasksResponse> = await axios.get(`${config.value('thePublisherUrl')}/tasks`, {
        params: cursor ? { cursor } : undefined,
      });
      allTasks.push(...response.data.data);
      cursor = response.data.cursor;
    } while (cursor);
    tasks.value = allTasks.filter(isWithinRetentionWindow);
    for (const task of tasks.value) {
      try {
        if (task.studioJobId) {
          const jobResp = await axios.get<Job>(`${config.value('studioJobsUrl')}/jobs/${task.studioJobId}`);
          studioJobs.value[task.studioJobId!] = jobResp.data;
        }
        if (task.epubGeneratorExecutionName) {
          const execResp = await axios.post<{ payload: GetExecutionInfoResponse }>(
            `${config.value('epubGuideGeneratorUrl')}/getExecutionInfo`,
            {
              key: task.epubGeneratorExecutionName,
            },
          );
          epubExecutions.value[task.epubGeneratorExecutionName!] = execResp.data.payload;
        }
        const res = await axios.get<BookV1>(`${config.value('booksContentRegistryUrl')}/${task.contentId}`);
        if (res.status === 200 && res.data.type === 'BookV1') {
          books.value[task.contentId] = res.data as BookV1;
        }
      } catch (error) {
        if ((error as any).status !== 404) {
          console.error(`Error fetching data for task ${task.id}:`, error);
        }
      }
    }
  } finally {
    isLoadingTasks.value = false;
  }
};

onMounted(() => {
  refresh();
});

const isStaging = computed(() => process.env.VUE_APP_ENVIRONMENT === 'staging');

const headers = [
  { text: 'ID', value: 'id' },
  { text: 'Name', value: 'name' },
  { text: 'Status', value: 'status' },
  { text: 'Language', value: 'language' },
  { text: 'Created', value: 'createdAt' },
  { text: 'Content Type', value: 'contentType' },
  { text: 'Content Id', value: 'contentId' },
  { text: '', value: 'action' },
];

const regenerateInProgress = ref(false);

async function regenerate(task: Task) {
  try {
    regenerateInProgress.value = true;
    await axios.post(`${config.value('thePublisherUrl')}/tasks/${task.id}/regenerate`);
  } finally {
    regenerateInProgress.value = false;
  }
}

return { __sfc: true,isLoadingTasks, tasks, studioJobs, epubExecutions, books, route, bookIdParameter, langParameter, pagesParameter, titleParameter, tabParameter, TASK_RETENTION_DAYS, isWithinRetentionWindow, refresh, isStaging, headers, regenerateInProgress, regenerate, IdIcon, CreateNewPublisherTaskForm }
}

})